/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  Renderer2,
} from '@angular/core';
import { TreeDraggedElement } from '@ali-hm/angular-tree-component';
import { constant, isFunction } from 'lodash';

const DRAG_OVER_CLASS = 'is-dragging-over';
const DRAG_DISABLED_CLASS = 'is-dragging-over-disabled';
const DRAG_DISABLED_BODY_CLASS = 'cursor-not-allowed-nested';

/**
 * This directive is based on <code>treeDrop</code> directive.
 * Unlike <code>treeDrop</code> directive it allows non-native DnD operation,
 * which are very important in heteragenous environment
 */
@Directive({
  standalone: false,
  selector: '[ghTreeDrop]',
})
export class TreeDropDirective implements OnDestroy {
  @Output('ghTreeDrop') drop = new EventEmitter();
  @Output('treeDropDragOver') dragOver = new EventEmitter();
  @Output('treeDropDragLeave') dragLeave = new EventEmitter();
  @Output('treeDropDragEnter') dragEnter = new EventEmitter();

  private _allowDrop: PredicateFn2<any, any> = constant(true);
  private dropDisabled = false;

  @Input()
  set treeAllowDrop(allowDrop: PredicateFn2<any, any> | boolean) {
    this._allowDrop = isFunction(allowDrop) ? allowDrop : constant(allowDrop);
  }

  constructor(private el: ElementRef,
              private renderer: Renderer2,
              private treeDraggedElement: TreeDraggedElement) {
  }

  ngOnDestroy(): void {
    if (this.dropDisabled) {
      this.removeDisabledClass();
    }
  }

  allowDrop($event: any): boolean {
    return this._allowDrop(this.treeDraggedElement.get(), $event);
  }

  @HostListener('mousemove', ['$event'])
  onDragOver($event: any): void {
    if (!this.treeDraggedElement.isDragging()) {
      return;
    }
    if (!this.allowDrop($event)) {
      return this.addDisabledClass();
    }

    this.dragOver.emit({ event: $event, element: this.treeDraggedElement.get() });

    $event.preventDefault();
    this.addClass();
  }

  @HostListener('mouseenter', ['$event'])
  onDragEnter($event: any): void {
    if (!this.treeDraggedElement.isDragging()) {
      return;
    }
    if (!this.allowDrop($event)) {
      return;
    }

    this.dragEnter.emit({ event: $event, element: this.treeDraggedElement.get() });
  }

  @HostListener('mouseleave', ['$event'])
  onDragLeave($event: any): void {
    if (!this.treeDraggedElement.isDragging()) {
      return;
    }
    if (!this.allowDrop($event)) {
      return this.removeDisabledClass();
    }

    this.dragLeave.emit({ event: $event, element: this.treeDraggedElement.get() });

    this.removeClass();
  }

  @HostListener('pointerup', ['$event'])
  onPointerUp($event: any): void {
    this.onDrop($event);
  }

  @HostListener('mouseup', ['$event'])
  onMouseUp($event: any): void {
    this.onDrop($event);
  }

  private onDrop($event: any): void {
    if (!this.treeDraggedElement.isDragging()) {
      return;
    }
    if (!this.allowDrop($event)) {
      this.removeDisabledClass();
      return;
    }

    $event.preventDefault();
    const payload = { event: $event, element: this.treeDraggedElement.get() };
    try {
      this.drop.emit(payload);
    } finally {
      this.removeClass();
      this.treeDraggedElement.set(void 0);
    }
  }

  private addClass(): void {
    this.renderer.addClass(this.el.nativeElement, DRAG_OVER_CLASS);
  }

  private removeClass(): void {
    this.renderer.removeClass(this.el.nativeElement, DRAG_OVER_CLASS);
  }

  private addDisabledClass(): void {
    this.dropDisabled = true;
    this.renderer.addClass(document.body, DRAG_DISABLED_BODY_CLASS);
    this.renderer.addClass(this.el.nativeElement, DRAG_DISABLED_CLASS);
  }

  private removeDisabledClass(): void {
    this.dropDisabled = false;
    this.renderer.removeClass(document.body, DRAG_DISABLED_BODY_CLASS);
    this.renderer.removeClass(this.el.nativeElement, DRAG_DISABLED_CLASS);
  }
}
